import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Brain, Play, CheckCircle, XCircle, Clock, Target, Zap,
  ChevronRight, ChevronLeft, RotateCcw, TrendingUp, AlertCircle,
  Loader2, BookOpen, Star, Award, Lightbulb, ArrowLeft, Check, X,
  Flame, Trophy, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import api from '../../api';

// ── Math Renderer ─────────────────────────────────────────────────────────────
function MathText({ text, className = '' }) {
  if (!text) return null;
  const parts = [];
  let remaining = text;
  let key = 0;
  while (remaining.length > 0) {
    const blockStart = remaining.indexOf('$$');
    const inlineStart = remaining.indexOf('$');
    if (blockStart !== -1 && blockStart <= inlineStart) {
      if (blockStart > 0) parts.push(<span key={key++}>{remaining.slice(0, blockStart)}</span>);
      const blockEnd = remaining.indexOf('$$', blockStart + 2);
      if (blockEnd === -1) { parts.push(<span key={key++}>{remaining}</span>); break; }
      try { parts.push(<BlockMath key={key++} math={remaining.slice(blockStart + 2, blockEnd)} />); }
      catch { parts.push(<span key={key++}>{remaining.slice(blockStart, blockEnd + 2)}</span>); }
      remaining = remaining.slice(blockEnd + 2);
    } else if (inlineStart !== -1) {
      if (inlineStart > 0) parts.push(<span key={key++}>{remaining.slice(0, inlineStart)}</span>);
      const inlineEnd = remaining.indexOf('$', inlineStart + 1);
      if (inlineEnd === -1) { parts.push(<span key={key++}>{remaining}</span>); break; }
      try { parts.push(<InlineMath key={key++} math={remaining.slice(inlineStart + 1, inlineEnd)} />); }
      catch { parts.push(<span key={key++}>{remaining.slice(inlineStart, inlineEnd + 1)}</span>); }
      remaining = remaining.slice(inlineEnd + 1);
    } else {
      parts.push(<span key={key++}>{remaining}</span>);
      break;
    }
  }
  return <span className={className}>{parts}</span>;
}

// ── Streak Celebration Modal ──────────────────────────────────────────────────
function StreakCelebrationModal({ reward, onClose }) {
  if (!reward) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      data-testid="streak-celebration-modal">
      {/* Confetti particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(24)].map((_, i) => (
          <div key={i} className="absolute animate-bounce rounded-full"
            style={{
              width: `${6 + (i % 5) * 3}px`,
              height: `${6 + (i % 5) * 3}px`,
              top: `${Math.random() * 90}%`,
              left: `${Math.random() * 95}%`,
              background: ['#f59e0b','#3b82f6','#10b981','#ec4899','#8b5cf6','#f97316'][i % 6],
              animationDelay: `${(i * 0.12)}s`,
              animationDuration: `${0.8 + (i % 4) * 0.3}s`,
              opacity: 0.85,
            }} />
        ))}
      </div>

      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-2xl p-8 max-w-sm w-full text-center animate-bounce"
        style={{ animationDuration: '0.6s', animationIterationCount: 1 }}>
        {/* Big emoji badge */}
        <div className="w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center text-5xl shadow-lg"
          style={{ background: 'linear-gradient(135deg, #fbbf24, #f59e0b)' }}>
          {reward.trophy_emoji}
        </div>

        <div className="text-xs font-bold text-amber-600 uppercase tracking-widest mb-1">
          Streak Badge Unlocked!
        </div>
        <h2 className="text-2xl font-black text-slate-800 mb-1">{reward.trophy_name}</h2>
        <p className="text-sm text-slate-500 mb-4">{reward.description}</p>

        {/* XP pill */}
        <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-2 mb-5">
          <Sparkles className="w-4 h-4 text-amber-500" />
          <span className="font-black text-amber-700 text-lg">+{reward.xp_awarded} XP</span>
        </div>

        {/* Streak count */}
        <div className="flex items-center justify-center gap-1.5 text-orange-600 font-bold text-sm mb-6">
          <Flame className="w-4 h-4" />
          {reward.streak} day streak — keep it going!
        </div>

        <button onClick={onClose} data-testid="close-streak-modal"
          className="w-full py-3 bg-[#2563eb] text-white rounded-2xl font-bold text-sm
            hover:bg-blue-700 transition-all">
          Awesome! Continue
        </button>
      </div>
    </div>
  );
}

// ── Streak Banner (compact, shown in setup screen) ────────────────────────────
function StreakBanner({ streak, nextMilestone }) {
  if (streak === 0 && !nextMilestone) return null;
  return (
    <div className="flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-2xl px-4 py-3 mb-5"
      data-testid="streak-banner">
      <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
        <Flame className={`w-5 h-5 ${streak > 0 ? 'text-orange-500' : 'text-slate-400'}`} />
      </div>
      <div className="flex-1 min-w-0">
        {streak > 0 ? (
          <>
            <p className="font-bold text-sm text-orange-700">{streak}-day streak!</p>
            {nextMilestone && (
              <p className="text-xs text-slate-500 mt-0.5">
                {nextMilestone.days_away} more day{nextMilestone.days_away > 1 ? 's' : ''} to earn <strong>{nextMilestone.emoji} {nextMilestone.name}</strong> +{nextMilestone.xp || ''}XP
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-500">
            Start your streak! Practice daily to earn badges. First badge at <strong>7 days</strong> {nextMilestone?.emoji}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Timer Hook ────────────────────────────────────────────────────────────────
function useTimer(initialSecs, onExpire, running = true, resetKey = 0) {
  const [secs, setSecs] = useState(initialSecs);
  const ref = useRef(null);
  useEffect(() => { setSecs(initialSecs); }, [initialSecs, resetKey]);
  useEffect(() => {
    if (!running) { clearInterval(ref.current); return; }
    ref.current = setInterval(() => {
      setSecs(s => {
        if (s <= 1) { clearInterval(ref.current); onExpire(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(ref.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, initialSecs, resetKey]);
  return secs;
}

const fmt = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

// ── Subject Topics Map ────────────────────────────────────────────────────────
const SUBJECT_TOPICS = {
  'Mathematics':          ['Algebra', 'Geometry', 'Trigonometry', 'Calculus', 'Statistics', 'Vectors', 'Matrices', 'Sequences & Series', 'Probability', 'Number Theory'],
  'English Language':     ['Comprehension', 'Grammar', 'Vocabulary', 'Essay Writing', 'Oral English', 'Lexis & Structure', 'Figures of Speech'],
  'Physics':              ['Mechanics', 'Heat & Temperature', 'Waves & Sound', 'Electricity', 'Magnetism', 'Optics', 'Nuclear Physics', 'Motion'],
  'Chemistry':            ['Organic Chemistry', 'Inorganic Chemistry', 'Electrochemistry', 'Chemical Kinetics', 'Acid-Base Reactions', 'Periodic Table'],
  'Biology':              ['Cell Biology', 'Genetics', 'Ecology', 'Human Physiology', 'Evolution', 'Classification', 'Nutrition', 'Reproduction'],
  'Geography':            ['Physical Geography', 'Human Geography', 'Maps & Scale', 'Climate & Weather', 'Population', 'Minerals & Resources'],
  'History':              ['Pre-Colonial Nigeria', 'Colonial Period', 'Independence', 'African History', 'World War History'],
  'Government':           ['Nigerian Constitution', 'Federalism', 'Democracy', 'Electoral System', 'Political Parties', 'Foreign Policy'],
  'Economics':            ['Demand & Supply', 'Market Structures', 'National Income', 'International Trade', 'Money & Banking', 'Government Finance'],
  'Agricultural Science': ['Crop Science', 'Animal Science', 'Soil Science', 'Farm Management', 'Agricultural Tools'],
  'Computer Science':     ['Algorithms', 'Programming Basics', 'Data Structures', 'Networking', 'Database', 'Operating Systems'],
  'Further Mathematics':  ['Complex Numbers', 'Linear Algebra', 'Differential Equations', 'Integration', '3D Vectors', 'Permutations & Combinations'],
};
const ALL_SUBJECTS = Object.keys(SUBJECT_TOPICS);

const DIFF_OPTS = [
  { value: 'easy',   label: 'Easy',   desc: 'Fundamental concepts', color: 'bg-emerald-100 border-emerald-400 text-emerald-700' },
  { value: 'medium', label: 'Medium', desc: 'Standard exam level',   color: 'bg-blue-100 border-blue-400 text-blue-700' },
  { value: 'hard',   label: 'Hard',   desc: 'WAEC/JAMB challenge',   color: 'bg-rose-100 border-rose-400 text-rose-700' },
];

// ── Screen: Setup ─────────────────────────────────────────────────────────────
function SetupScreen({ onStart }) {
  const [subject,    setSubject]    = useState('Mathematics');
  const [topics,     setTopics]     = useState([]);
  const [difficulty, setDifficulty] = useState('medium');
  const [count,      setCount]      = useState(10);
  const [loading,    setLoading]    = useState(false);

  const availTopics = SUBJECT_TOPICS[subject] || [];
  const toggleTopic = t =>
    setTopics(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

  const handleStart = async () => {
    if (topics.length === 0) { toast.error('Select at least one topic'); return; }
    setLoading(true);
    try {
      const res = await api.post('/exam-prep/generate', { subject, topics, difficulty, question_count: count });
      if (res.success) { onStart(res.data); }
      else throw new Error(res.message || 'Generation failed');
    } catch (e) {
      toast.error(e.message || 'AI generation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Reset topics when subject changes
  useEffect(() => { setTopics([]); }, [subject]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-32 gap-6">
      <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center animate-pulse">
        <Brain className="w-10 h-10 text-[#2563eb]" />
      </div>
      <div className="text-center">
        <p className="text-xl font-bold text-slate-800">AI is crafting your exam…</p>
        <p className="text-slate-500 mt-1">Generating {count} {difficulty} questions on {topics.join(', ')}</p>
      </div>
      <div className="flex gap-1.5">
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} className="w-2 h-2 rounded-full bg-[#2563eb] animate-bounce"
            style={{ animationDelay: `${i * 0.15}s` }} />
        ))}
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Subject */}
      <div data-testid="subject-selector-section">
        <label className="block text-sm font-bold text-slate-700 mb-3">1. Choose Subject</label>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {ALL_SUBJECTS.map(s => (
            <button key={s} onClick={() => setSubject(s)}
              data-testid={`subject-btn-${s.replace(/\s+/g, '-').toLowerCase()}`}
              className={`px-3 py-2 rounded-xl text-xs font-semibold border-2 transition-all ${
                subject === s
                  ? 'bg-[#2563eb] border-[#2563eb] text-white shadow-md'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-[#2563eb] hover:text-[#2563eb]'
              }`}>{s}</button>
          ))}
        </div>
      </div>

      {/* Topics */}
      <div data-testid="topic-selector-section">
        <label className="block text-sm font-bold text-slate-700 mb-3">
          2. Select Topics <span className="text-slate-400 font-normal">({topics.length} selected)</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {availTopics.map(t => (
            <button key={t} onClick={() => toggleTopic(t)}
              data-testid={`topic-chip-${t.replace(/\s+/g, '-').toLowerCase()}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-all ${
                topics.includes(t)
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-blue-400'
              }`}>{t}</button>
          ))}
        </div>
        {topics.length === 0 && (
          <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5" /> Select at least one topic to continue
          </p>
        )}
      </div>

      {/* Difficulty */}
      <div data-testid="difficulty-selector-section">
        <label className="block text-sm font-bold text-slate-700 mb-3">3. Choose Difficulty</label>
        <div className="grid grid-cols-3 gap-3">
          {DIFF_OPTS.map(d => (
            <button key={d.value} onClick={() => setDifficulty(d.value)}
              data-testid={`difficulty-btn-${d.value}`}
              className={`p-3 rounded-xl border-2 text-center transition-all ${
                difficulty === d.value ? d.color + ' shadow-md' : 'bg-white border-slate-200 hover:border-slate-300'
              }`}>
              <div className="font-bold text-sm">{d.label}</div>
              <div className="text-xs opacity-70 mt-0.5">{d.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Count */}
      <div>
        <label className="block text-sm font-bold text-slate-700 mb-3">
          4. Number of Questions: <span className="text-[#2563eb]">{count}</span>
        </label>
        <input type="range" min={5} max={20} step={5} value={count}
          onChange={e => setCount(Number(e.target.value))}
          data-testid="question-count-slider"
          className="w-full accent-blue-600" />
        <div className="flex justify-between text-xs text-slate-400 mt-1">
          <span>5 (Quick)</span><span>10</span><span>15</span><span>20 (Full)</span>
        </div>
      </div>

      <button onClick={handleStart} disabled={topics.length === 0}
        data-testid="start-exam-prep-btn"
        className="w-full py-4 bg-[#2563eb] text-white rounded-2xl font-bold text-lg
          hover:bg-blue-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed
          flex items-center justify-center gap-3">
        <Brain className="w-5 h-5" />
        Generate My Practice Exam
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
}

// ── Screen: Quiz ──────────────────────────────────────────────────────────────
function QuizScreen({ session, onComplete }) {
  const questions = session.questions || [];
  const [idx,      setIdx]      = useState(0);
  const [answers,  setAnswers]  = useState(() => new Array(questions.length).fill(-1));
  const [elapsed,  setElapsed]  = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const qKey = idx;
  const SESSION_SECS = questions.length * 90;
  const Q_SECS = 75;

  // Session timer
  const sesRef = useRef(null);
  useEffect(() => {
    sesRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(sesRef.current);
  }, []);

  const handleSubmit = useCallback(async (forcedAnswers) => {
    clearInterval(sesRef.current);
    const finalAnswers = forcedAnswers || answers;
    setSubmitting(true);
    try {
      const res = await api.post(`/exam-prep/sessions/${session.id}/submit`, {
        answers: finalAnswers,
        time_seconds: elapsed,
      });
      if (res.success) onComplete(res.data);
      else throw new Error(res.message);
    } catch (e) {
      toast.error('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [session.id, answers, elapsed, onComplete]);

  const sessionExpire = useCallback(() => handleSubmit(answers), [handleSubmit, answers]);
  const qExpire = useCallback(() => {
    if (idx < questions.length - 1) setIdx(i => i + 1);
    else handleSubmit(answers);
  }, [idx, questions.length, handleSubmit, answers]);

  const sessionLeft = SESSION_SECS - elapsed;
  useTimer(Q_SECS, qExpire, true, qKey);

  const q = questions[idx];
  if (!q) return null;
  const answered = answers.filter(a => a !== -1).length;

  return (
    <div className="max-w-2xl mx-auto" data-testid="exam-prep-quiz-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 bg-white rounded-2xl px-4 py-3 shadow-sm border border-slate-100">
        <div className="flex items-center gap-2 text-slate-600 text-sm">
          <Target className="w-4 h-4 text-[#2563eb]" />
          <span className="font-semibold">{session.subject}</span>
          <span className="text-slate-300">|</span>
          <span className="capitalize">{session.difficulty}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-1 text-sm font-bold ${sessionLeft < 60 ? 'text-rose-600' : 'text-slate-700'}`}>
            <Clock className="w-4 h-4" /> {fmt(Math.max(0, sessionLeft))}
          </div>
          <span className="text-xs text-slate-400">{answered}/{questions.length}</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-slate-200 rounded-full h-1.5 mb-5">
        <div className="bg-[#2563eb] h-1.5 rounded-full transition-all"
          style={{ width: `${(idx / questions.length) * 100}%` }} />
      </div>

      {/* Question */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-4">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="flex-1">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Q{idx + 1} of {questions.length}
            </span>
            <div className="mt-2 text-slate-800 font-medium text-[15px] leading-relaxed">
              <MathText text={q.question_text} />
            </div>
          </div>
        </div>

        {/* Options */}
        <div className="space-y-2.5">
          {(q.options || []).map((opt, oi) => (
            <button key={oi}
              onClick={() => setAnswers(prev => { const next = [...prev]; next[idx] = oi; return next; })}
              data-testid={`option-${oi}`}
              className={`w-full text-left px-4 py-3.5 rounded-xl border-2 transition-all font-medium text-sm ${
                answers[idx] === oi
                  ? 'bg-blue-50 border-blue-500 text-blue-800 shadow-sm'
                  : 'bg-slate-50 border-slate-200 text-slate-700 hover:border-blue-300 hover:bg-blue-50/50'
              }`}>
              <MathText text={opt} />
            </button>
          ))}
        </div>
      </div>

      {/* Nav */}
      <div className="flex items-center gap-3">
        <button onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0}
          data-testid="prev-question-btn"
          className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-semibold
            hover:bg-slate-200 transition-all disabled:opacity-30 flex items-center justify-center gap-2">
          <ChevronLeft className="w-4 h-4" /> Prev
        </button>
        {idx < questions.length - 1 ? (
          <button onClick={() => setIdx(i => i + 1)}
            data-testid="next-question-btn"
            className="flex-2 flex-1 py-3 bg-[#2563eb] text-white rounded-xl font-semibold
              hover:bg-blue-700 transition-all flex items-center justify-center gap-2">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={() => handleSubmit()} disabled={submitting}
            data-testid="submit-exam-btn"
            className="flex-2 flex-1 py-3 bg-emerald-600 text-white rounded-xl font-semibold
              hover:bg-emerald-700 transition-all flex items-center justify-center gap-2 disabled:opacity-60">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Submit Exam
          </button>
        )}
      </div>
    </div>
  );
}

// ── Screen: Results ───────────────────────────────────────────────────────────
function ResultsScreen({ result, onNewSession, onGoHome }) {
  const [expanded, setExpanded] = useState(null);
  const pct = result.score_pct || 0;
  const correct = result.correct_count || 0;
  const total   = result.total_count || 0;
  const details = result.scored_details || [];

  const grade =
    pct >= 75 ? { label: 'Excellent', color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200' } :
    pct >= 60 ? { label: 'Good',      color: 'text-blue-600',    bg: 'bg-blue-50 border-blue-200' } :
    pct >= 50 ? { label: 'Average',   color: 'text-amber-600',   bg: 'bg-amber-50 border-amber-200' } :
                { label: 'Needs Work',color: 'text-rose-600',    bg: 'bg-rose-50 border-rose-200' };

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="exam-prep-results-screen">
      {/* Score Card */}
      <div className={`rounded-2xl border-2 p-6 text-center ${grade.bg}`}>
        <div className="text-5xl font-black mb-1 text-slate-800">{pct}%</div>
        <div className={`text-lg font-bold ${grade.color}`}>{grade.label}</div>
        <div className="text-sm text-slate-500 mt-1">{correct} / {total} correct • {result.subject}</div>
      </div>

      {/* Topics Summary */}
      {(result.strong_topics?.length > 0 || result.weak_topics?.length > 0) && (
        <div className="grid grid-cols-2 gap-3">
          {result.strong_topics?.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <span className="font-bold text-emerald-700 text-sm">Strong Topics</span>
              </div>
              {result.strong_topics.map(t => (
                <div key={t} className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
                  <Star className="w-3 h-3" /> {t}
                </div>
              ))}
            </div>
          )}
          {result.weak_topics?.length > 0 && (
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <AlertCircle className="w-4 h-4 text-rose-600" />
                <span className="font-bold text-rose-700 text-sm">Needs Practice</span>
              </div>
              {result.weak_topics.map(t => (
                <div key={t} className="text-xs text-rose-600 flex items-center gap-1 mt-1">
                  <Target className="w-3 h-3" /> {t}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <button onClick={onGoHome} data-testid="back-to-prep-btn"
          className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-all flex items-center justify-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Home
        </button>
        {result.weak_topics?.length > 0 && (
          <button onClick={() => onNewSession(result.subject, result.weak_topics)}
            data-testid="practice-weak-topics-btn"
            className="flex-1 py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-all flex items-center justify-center gap-2">
            <Target className="w-4 h-4" /> Practice Weak Topics
          </button>
        )}
        <button onClick={() => onNewSession(result.subject, [])}
          data-testid="new-session-btn"
          className="flex-1 py-3 bg-[#2563eb] text-white rounded-xl font-semibold hover:bg-blue-700 transition-all flex items-center justify-center gap-2">
          <RotateCcw className="w-4 h-4" /> New Session
        </button>
      </div>

      {/* Question Review */}
      <div>
        <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[#2563eb]" /> Review All Questions
        </h3>
        <div className="space-y-2.5">
          {details.map((d, i) => (
            <div key={i}
              className={`rounded-xl border overflow-hidden ${d.is_correct ? 'border-emerald-200 bg-emerald-50/50' : 'border-rose-200 bg-rose-50/50'}`}>
              <button onClick={() => setExpanded(expanded === i ? null : i)}
                data-testid={`review-q-${i}`}
                className="w-full text-left p-4 flex items-start gap-3">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${d.is_correct ? 'bg-emerald-500' : 'bg-rose-500'}`}>
                  {d.is_correct ? <Check className="w-3.5 h-3.5 text-white" /> : <X className="w-3.5 h-3.5 text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-700 leading-relaxed">
                    <MathText text={`Q${i + 1}: ${d.question_text}`} />
                  </div>
                  {expanded !== i && !d.is_correct && (
                    <div className="text-xs text-rose-600 mt-1">
                      Your answer: {d.options?.[d.student_answer] || 'Not answered'}
                    </div>
                  )}
                </div>
              </button>
              {expanded === i && (
                <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-2">
                  {(d.options || []).map((opt, oi) => (
                    <div key={oi} className={`text-xs px-3 py-2 rounded-lg flex items-start gap-2 ${
                      oi === d.correct_index ? 'bg-emerald-100 text-emerald-700 font-semibold' :
                      oi === d.student_answer && !d.is_correct ? 'bg-rose-100 text-rose-700 line-through' :
                      'text-slate-600'
                    }`}>
                      {oi === d.correct_index ? <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> :
                       oi === d.student_answer && !d.is_correct ? <X className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> :
                       <span className="w-3.5 h-3.5 flex-shrink-0" />}
                      <MathText text={opt} />
                    </div>
                  ))}
                  {d.explanation && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-700 flex items-start gap-2">
                      <Lightbulb className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                      <MathText text={d.explanation} />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Leaderboard Panel ─────────────────────────────────────────────────────────
function LeaderboardPanel() {
  const [data,      setData]    = useState(null);
  const [loading,   setLoading] = useState(true);
  const [subject,   setSubject] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = subject ? `/exam-prep/leaderboard?subject=${encodeURIComponent(subject)}` : '/exam-prep/leaderboard';
      const res = await api.get(url);
      if (res.success) setData(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [subject]);

  useEffect(() => { load(); }, [load]);

  const MEDAL = { 1: '🥇', 2: '🥈', 3: '🥉' };

  const board   = data?.leaderboard || [];
  const myEntry = data?.my_entry;

  return (
    <div data-testid="leaderboard-panel">
      {/* Subject filter */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {['', 'Mathematics', 'English Language', 'Physics', 'Chemistry', 'Biology'].map(s => (
          <button key={s} onClick={() => setSubject(s)}
            data-testid={`leaderboard-filter-${s || 'all'}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-all ${
              subject === s
                ? 'bg-[#2563eb] border-[#2563eb] text-white'
                : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300'
            }`}>{s || 'All Subjects'}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-7 h-7 text-[#2563eb] animate-spin" />
        </div>
      ) : board.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
          <Trophy className="w-10 h-10 text-slate-200 mx-auto mb-3" />
          <p className="font-bold text-slate-600">No prep sessions yet in your class</p>
          <p className="text-sm text-slate-400 mt-1">Be the first on the leaderboard!</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {/* Class info */}
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              {data?.class_name || 'Your Class'}
            </p>
            {data?.subject_filter && (
              <span className="text-xs text-[#2563eb] font-semibold">{data.subject_filter}</span>
            )}
          </div>

          {board.map(entry => (
            <div key={entry.rank}
              data-testid={entry.is_me ? 'leaderboard-my-entry' : `leaderboard-rank-${entry.rank}`}
              className={`flex items-center gap-3 p-4 rounded-2xl border-2 transition-all ${
                entry.is_me
                  ? 'bg-blue-50 border-blue-400 shadow-md'
                  : 'bg-white border-slate-100'
              }`}>
              {/* Rank */}
              <div className="w-8 text-center flex-shrink-0">
                {MEDAL[entry.rank]
                  ? <span className="text-xl">{MEDAL[entry.rank]}</span>
                  : <span className="text-sm font-black text-slate-400">#{entry.rank}</span>}
              </div>
              {/* Name */}
              <div className="flex-1 min-w-0">
                <p className={`font-bold text-sm truncate ${entry.is_me ? 'text-blue-800' : 'text-slate-800'}`}>
                  {entry.display_name} {entry.is_me && <span className="text-[10px] font-bold text-blue-500 ml-1">YOU</span>}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  {entry.sessions} session{entry.sessions !== 1 ? 's' : ''} • Best: {entry.best_score}%
                </p>
              </div>
              {/* Score */}
              <div className="text-right flex-shrink-0">
                <div className={`text-lg font-black ${entry.rank === 1 ? 'text-amber-600' : entry.rank <= 3 ? 'text-blue-600' : 'text-slate-700'}`}>
                  {entry.avg_score}%
                </div>
                <div className="text-[10px] text-slate-400">avg score</div>
              </div>
            </div>
          ))}

          {/* My entry if outside top 10 */}
          {myEntry && (
            <div data-testid="leaderboard-my-rank-outside"
              className="flex items-center gap-3 p-4 rounded-2xl border-2 border-dashed border-blue-300 bg-blue-50/50">
              <div className="w-8 text-center text-sm font-black text-slate-400 flex-shrink-0">
                #{myEntry.rank}
              </div>
              <div className="flex-1">
                <p className="font-bold text-sm text-blue-700">
                  You • Keep going to reach the top 10!
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">{myEntry.sessions} sessions</p>
              </div>
              <div className="text-lg font-black text-blue-600">{myEntry.avg_score}%</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── History Panel ─────────────────────────────────────────────────────────────
function HistoryPanel({ sessions, onContinue }) {
  if (!sessions.length) return (
    <div className="text-center py-10 text-slate-400">
      <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-50" />
      <p className="text-sm">No practice sessions yet. Generate your first exam above!</p>
    </div>
  );
  return (
    <div className="space-y-2.5" data-testid="history-panel">
      {sessions.map(s => (
        <div key={s.id} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-black text-sm flex-shrink-0 ${
            (s.score_pct || 0) >= 75 ? 'bg-emerald-100 text-emerald-700' :
            (s.score_pct || 0) >= 50 ? 'bg-blue-100 text-blue-700' :
            s.status === 'completed' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-500'
          }`}>
            {s.status === 'completed' ? `${s.score_pct || 0}%` : '...'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-slate-800 truncate">{s.subject}</p>
            <p className="text-xs text-slate-400 mt-0.5 truncate">
              {(s.topics || []).slice(0, 3).join(', ')} • {s.difficulty} • {(s.generated_at || '').slice(0, 10)}
            </p>
          </div>
          {s.status !== 'completed' && (
            <button onClick={() => onContinue(s)} data-testid={`continue-session-${s.id}`}
              className="text-xs font-semibold text-[#2563eb] hover:underline">Continue</button>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ExamPrepPage() {
  const navigate = useNavigate();
  const [screen,   setScreen]   = useState('home');  // home | quiz | results
  const [session,  setSession]  = useState(null);
  const [result,   setResult]   = useState(null);
  const [history,  setHistory]  = useState([]);
  const [tab,      setTab]      = useState('new');   // new | leaderboard | history
  const [streakData,   setStreakData]   = useState(null);
  const [celebration,  setCelebration] = useState(null); // streak_reward obj

  const loadHistory = async () => {
    try {
      const res = await api.get('/exam-prep/sessions?limit=20');
      if (res.success) setHistory(res.data);
    } catch { /* silent */ }
  };

  const loadStreak = async () => {
    try {
      const res = await api.get('/exam-prep/streak');
      if (res.success) setStreakData(res.data);
    } catch { /* silent */ }
  };

  useEffect(() => { loadHistory(); loadStreak(); }, []);

  const handleStart = (sess) => {
    setSession(sess);
    setScreen('quiz');
  };

  const handleComplete = (res) => {
    setResult(res);
    setScreen('results');
    loadHistory();
    loadStreak();
    // Show celebration if a streak milestone was hit
    if (res.streak_reward) {
      setCelebration(res.streak_reward);
    }
  };

  const handleNewSession = (subject, weakTopics) => {
    setScreen('home');
    setTab('new');
    setResult(null);
    setSession(null);
    // Could pre-fill subject/topics but let user choose
  };

  const handleContinue = (sess) => {
    setSession(sess);
    setScreen('quiz');
  };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      {/* Streak Celebration Modal */}
      <StreakCelebrationModal reward={celebration} onClose={() => setCelebration(null)} />
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/student/dashboard')}
          className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center shadow-md">
          <Brain className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-black text-slate-800">AI Exam Preparation</h1>
          <p className="text-xs text-slate-500">AI-generated practice sessions tailored for you</p>
        </div>
      </div>

      {/* Quiz Screen */}
      {screen === 'quiz' && session && (
        <QuizScreen session={session} onComplete={handleComplete} />
      )}

      {/* Results Screen */}
      {screen === 'results' && result && (
        <ResultsScreen result={result} onNewSession={handleNewSession} onGoHome={() => { setScreen('home'); setTab('new'); }} />
      )}

      {/* Home Screen */}
      {screen === 'home' && (
        <>
          {/* Tabs */}
          <div className="flex gap-1 bg-slate-100 rounded-2xl p-1 mb-6">
            {[
              { key: 'new',         label: 'New Session' },
              { key: 'leaderboard', label: 'Leaderboard' },
              { key: 'history',     label: `History (${history.length})` },
            ].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  tab === t.key ? 'bg-white shadow-sm text-[#2563eb]' : 'text-slate-500 hover:text-slate-700'
                }`}>{t.label}</button>
            ))}
          </div>

          {tab === 'new' && (
            <>
              <StreakBanner
                streak={streakData?.streak || 0}
                nextMilestone={streakData?.next_milestone}
              />
              <SetupScreen onStart={handleStart} />
            </>
          )}
          {tab === 'leaderboard' && <LeaderboardPanel />}
          {tab === 'history' && <HistoryPanel sessions={history} onContinue={handleContinue} />}
        </>
      )}
    </div>
  );
}
